/**
 * PIN Lock Screen Component
 *
 * Full-screen overlay that blocks access until correct PIN is entered.
 * Features:
 * - 4-digit PIN input with visual feedback
 * - Number pad for easy entry
 * - Shake animation on wrong PIN
 * - Barber name display
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Delete, LogOut } from 'lucide-react';
import { pinLockService } from '../../services/pinLockService';

interface PinLockScreenProps {
  onUnlock: () => void;
  onLogout?: () => void;
  barberName?: string;
}

const PinLockScreen: React.FC<PinLockScreenProps> = ({
  onUnlock,
  onLogout,
  barberName = 'Barber',
}) => {
  const [pin, setPin] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [shake, setShake] = useState<boolean>(false);

  // Handle PIN digit entry
  const handleDigitPress = useCallback((digit: string) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      setError(false);

      // Auto-verify when 4 digits entered. BEHAVIOR-PIN (2026-09-29): verifyPin is now async (server
      // verification against the hashed secret — no raw PIN on the client).
      if (newPin.length === 4) {
        setTimeout(async () => {
          const isValid = await pinLockService.verifyPin(newPin);
          if (isValid) {
            onUnlock();
          } else {
            setError(true);
            setShake(true);
            setPin('');
            setTimeout(() => setShake(false), 500);
          }
        }, 100);
      }
    }
  }, [pin, onUnlock]);

  // Handle backspace
  const handleBackspace = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
    setError(false);
  }, []);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handleDigitPress(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDigitPress, handleBackspace]);

  // Number pad buttons
  const numberPad = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['', '0', 'del'],
  ];

  return (
    // SCROLLABLE (Jose 2026-10-23): center when it fits, scroll when it doesn't, so the PIN pad + buttons
    // are never center-clipped off a shorter iPad screen (matches StationLoginScreen).
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 overflow-y-auto overscroll-contain">
    <div className="min-h-full flex flex-col items-center justify-center p-4 py-8">
      {/* Lock Icon */}
      <div className="mb-8">
        <div className="w-20 h-20 rounded-full bg-purple-600/20 flex items-center justify-center">
          <Lock className="w-10 h-10 text-purple-400" />
        </div>
      </div>

      {/* Welcome Text */}
      <h1 className="text-2xl font-bold text-white mb-2">Welcome Back</h1>
      <p className="text-purple-300 mb-8">{barberName}</p>

      {/* PIN Dots */}
      <div
        className={`flex gap-4 mb-8 ${shake ? 'animate-shake' : ''}`}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`w-4 h-4 rounded-full transition-all duration-200 ${
              i < pin.length
                ? error
                  ? 'bg-red-500 scale-110'
                  : 'bg-purple-500 scale-110'
                : 'bg-gray-600'
            }`}
          />
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-red-400 text-sm mb-4 animate-pulse">
          Incorrect PIN. Please try again.
        </p>
      )}

      {/* Number Pad */}
      <div className="grid gap-3">
        {numberPad.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-3">
            {row.map((key, keyIndex) => {
              if (key === '') {
                return <div key={keyIndex} className="w-20 h-16" />;
              }

              if (key === 'del') {
                return (
                  <button
                    key={keyIndex}
                    onClick={handleBackspace}
                    className="w-20 h-16 rounded-xl bg-gray-800 hover:bg-gray-700 active:bg-gray-600 flex items-center justify-center transition-colors"
                  >
                    <Delete className="w-6 h-6 text-gray-400" />
                  </button>
                );
              }

              return (
                <button
                  key={keyIndex}
                  onClick={() => handleDigitPress(key)}
                  className="w-20 h-16 rounded-xl bg-gray-800 hover:bg-gray-700 active:bg-purple-700 text-white text-2xl font-semibold transition-colors"
                >
                  {key}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Logout Option */}
      {onLogout && (
        <button
          onClick={onLogout}
          className="mt-8 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm">Switch User</span>
        </button>
      )}

      {/* Style for shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
    </div>
  );
};

export default PinLockScreen;
